import React, { useEffect } from "react";
import { lock, unlock } from "./scrollLock";
import { X } from "lucide-react";

export const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    sections: [
      {
        h: "1. Introduction",
        p: "Misty Distribution (\"we\", \"us\", \"our\") respects your privacy and is committed to protecting your personal information in accordance with South Africa's Protection of Personal Information Act, 2013 (POPIA).",
      },
      {
        h: "2. Information We Collect",
        p: "We collect the following categories of personal information when you interact with our website: name and surname, email address, phone number, the role you indicated (Distributor, Retailer, Individual, Other), the contents of any message you send to us, your IP address and basic browser information, and your age confirmation when entering the site.",
      },
      {
        h: "3. How We Use Your Information",
        p: "We use your personal information solely to respond to enquiries, to provide information about our products to verified adult users (18+), to maintain the security and integrity of our website, and to comply with applicable South African law. We do not use your information for direct marketing without your consent.",
      },
      {
        h: "4. We Do Not Sell Your Data",
        p: "Misty Distribution does not sell, rent, or trade your personal information to any third party for marketing purposes.",
      },
      {
        h: "5. Third-Party Processors",
        p: "We may share information with trusted operators that help us run our website (such as hosting and email infrastructure providers). These operators process information only on our behalf and under written instruction, subject to confidentiality and POPIA-compliant safeguards.",
      },
      {
        h: "6. Retention",
        p: "We retain enquiry information for a maximum of three (3) years from the date of last contact, after which it is securely deleted unless we are required by law to retain it for a longer period.",
      },
      {
        h: "7. Your Rights",
        p: "Under POPIA you have the right to access the personal information we hold about you, to request correction or deletion of inaccurate or outdated information, to object to processing, to withdraw consent at any time, and to lodge a complaint with the Information Regulator (South Africa).",
      },
      {
        h: "8. Cookies",
        p: "Our site uses only essential cookies required for the age gate and for site functionality. We do not use advertising or cross-site tracking cookies.",
      },
      {
        h: "9. Security",
        p: "We use commercially reasonable technical and organisational measures to protect your information against loss, misuse, or unauthorised access, including encryption in transit and access controls on stored data.",
      },
      {
        h: "10. No Minors",
        p: "This website and our products are strictly for adults aged 18 and older. We do not knowingly collect information from anyone under 18. If we discover such information has been collected, we will delete it immediately.",
      },
      {
        h: "11. Contact",
        p: "Information requests, deletions, or complaints can be sent to info@mistydistribution.com, or by post to: Misty Distribution, 250 Hall Street, Westend Office Park, Building E, Centurion, 0157.",
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    sections: [
      {
        h: "1. Adults Only (18+)",
        p: "Access to and use of this website is restricted to persons who are 18 years of age or older. By entering the website you confirm that you meet this requirement. We reserve the right to deny access to anyone who cannot verify their age.",
      },
      {
        h: "2. Informational Website Only",
        p: "This website is provided for informational purposes only. Misty Distribution does not sell products directly to consumers through this website. Product availability, pricing, and stock are managed through our distributor and retailer network.",
      },
      {
        h: "3. Nicotine Warning",
        p: "Products referenced on this website contain nicotine. Nicotine is an addictive chemical. Vaping products are not suitable for non-smokers, pregnant or breastfeeding women, or persons with heart disease or other medical conditions. Consult a healthcare professional before use.",
      },
      {
        h: "4. Intellectual Property",
        p: "All trademarks, logos, brand names, product images, written content, and design elements on this website are the property of Misty Distribution or its brand partners and are protected by South African and international intellectual property law. No content may be reproduced, distributed, or used commercially without our prior written permission.",
      },
      {
        h: "5. Limitation of Liability",
        p: "The website and its content are provided \"as is\" without warranties of any kind. To the maximum extent permitted by law, Misty Distribution shall not be liable for any direct, indirect, incidental, or consequential loss arising from use of, or inability to use, this website or any product referenced on it.",
      },
      {
        h: "6. Third-Party Links",
        p: "This website may link to third-party websites (such as our Instagram pages). We are not responsible for the content, privacy practices, or accuracy of any third-party site.",
      },
      {
        h: "7. Changes",
        p: "We may update these Terms from time to time. Continued use of the website after changes are posted constitutes acceptance of the revised Terms.",
      },
      {
        h: "8. Governing Law",
        p: "These Terms are governed by the laws of the Republic of South Africa. Any disputes shall be subject to the exclusive jurisdiction of the South African courts.",
      },
    ],
  },
  age: {
    title: "Age Restriction Policy",
    sections: [
      {
        h: "1. Our Commitment",
        p: "Misty Distribution is firmly committed to preventing access to our products, brand content, and website by anyone under the age of 18. We do not market, sell, or distribute vaping products to minors under any circumstances.",
      },
      {
        h: "2. Age Verification on Entry",
        p: "Every visitor to our website is required to confirm they are 18 years of age or older before any branded content is displayed. Visitors who decline are returned to their previous page.",
      },
      {
        h: "3. Distributor & Retailer Obligations",
        p: "All authorised distributors and retailers stocking our brands are contractually required to verify the age of every purchaser, refuse sale to minors, comply with applicable South African legislation, and display required warning signage at the point of sale.",
      },
      {
        h: "4. Compliance with South African Law",
        p: "We support and comply with the Tobacco Products and Electronic Delivery Systems Control Act and all applicable South African regulations governing the sale, marketing, and distribution of nicotine-containing vapour products.",
      },
      {
        h: "5. No Youth Marketing",
        p: "Our branding, packaging, communication, and visual identity are designed for and directed at adult vapers and existing adult smokers seeking alternatives. We do not use imagery, language, mascots, or channels that target or appeal to minors.",
      },
      {
        h: "6. Reporting",
        p: "If you become aware of any sale of our products to a minor, or any marketing that appears directed at minors, please report it immediately to info@mistydistribution.com. Reports are treated confidentially and acted on promptly.",
      },
      {
        h: "7. Enforcement",
        p: "Any distributor or retailer found to be in violation of this policy may have their supply agreement suspended or terminated and may be reported to the relevant authorities.",
      },
    ],
  },
};

export const PolicyModal = ({ policyKey, onClose }) => {
  useEffect(() => {
    if (!policyKey) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    lock("policy-modal");
    return () => {
      document.removeEventListener("keydown", onKey);
      unlock("policy-modal");
    };
  }, [policyKey, onClose]);

  if (!policyKey) return null;
  const policy = POLICIES[policyKey];
  if (!policy) return null;

  return (
    <div
      className="fixed inset-0 z-[150] bg-misty-base/95 backdrop-blur-md overflow-y-auto"
      data-testid="policy-modal-overlay"
      onClick={onClose}
    >
      <div
        className="min-h-full flex items-start justify-center p-4 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="bg-misty-surface border border-white/10 w-full max-w-3xl my-8 relative"
          data-testid={`policy-modal-${policyKey}`}
        >
          <div className="sticky top-0 bg-misty-surface border-b border-white/10 px-8 sm:px-12 py-6 flex items-start justify-between gap-4 z-10">
            <div className="flex flex-col gap-2">
              <p className="text-misty-steel text-[10px] tracking-[0.4em] uppercase">
                Legal
              </p>
              <h3 className="font-display text-3xl sm:text-4xl text-misty-text tracking-wide leading-none">
                {policy.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-misty-muted hover:text-misty-text transition-colors p-2 -mr-2"
              data-testid="policy-modal-close"
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </div>

          <div className="px-8 sm:px-12 py-8 flex flex-col gap-8">
            {policy.sections.map((s, i) => (
              <div key={i} className="flex flex-col gap-3">
                <h4 className="font-sans text-misty-text text-sm tracking-[0.15em] uppercase font-semibold">
                  {s.h}
                </h4>
                <p className="text-misty-muted leading-relaxed text-sm sm:text-base">
                  {s.p}
                </p>
              </div>
            ))}
            <p className="text-misty-dim text-[10px] tracking-[0.3em] uppercase pt-4 border-t border-white/5">
              Press ESC to close
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
